import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/layout/PublicLayout';
import QuickTestModal from '../../components/QuickTestModal';
import WhaleLogo from '../../components/WhaleLogo';

const testimonials = [
  {
    name: 'Maria Santos',
    role: 'IELTS Student',
    country: 'Brazil',
    image: '🇧🇷',
    score: 'Band 7.5',
    text: 'I improved my IELTS Speaking score from 6.0 to 7.5 in just 2 months! The phoneme-level feedback helped me identify exactly which sounds I was mispronouncing.',
  },
  {
    name: 'Kenji Tanaka',
    role: 'Software Engineer',
    country: 'Japan',
    image: '🇯🇵',
    score: 'Got the job!',
    text: 'After practicing interview questions for 3 weeks, I felt confident in my technical interviews. The AI feedback on my pronunciation made a huge difference.',
  },
  {
    name: 'Anna Kowalski',
    role: 'Medical Professional',
    country: 'Poland',
    image: '🇵🇱',
    score: '95% accuracy',
    text: 'As a doctor, clear communication is critical. This app helped me reduce my accent and speak more clearly with patients. Highly recommend!',
  },
  {
    name: 'Carlos Rodriguez',
    role: 'Business Executive',
    country: 'Mexico',
    image: '🇲🇽',
    score: 'Promoted!',
    text: 'I needed to present to international clients. After 1 month of practice, my presentations became so much clearer. Got promoted to regional manager!',
  },
  {
    name: 'Sophie Chen',
    role: 'University Student',
    country: 'China',
    image: '🇨🇳',
    score: 'Band 8.0',
    text: 'The detailed feedback on each word helped me understand my weak points. My IELTS speaking went from 6.5 to 8.0. Absolutely worth it!',
  },
  {
    name: 'Ahmed Hassan',
    role: 'PhD Researcher',
    country: 'Egypt',
    image: '🇪🇬',
    score: 'Conference ready',
    text: 'I was nervous about presenting my research in English. This app helped me practice until I felt completely confident. My presentation was a success!',
  },
  {
    name: 'Priya Sharma',
    role: 'IT Professional',
    country: 'India',
    image: '🇮🇳',
    score: 'Client approved',
    text: 'Client calls used to stress me out. Now I speak clearly and confidently. My manager noticed the improvement and I got assigned to bigger projects.',
  },
  {
    name: 'Olga Petrova',
    role: 'Teacher',
    country: 'Ukraine',
    image: '🇺🇦',
    score: 'Native-like',
    text: 'As an English teacher, I wanted to perfect my pronunciation. The AI caught mistakes I never knew I was making. My students notice the difference!',
  },
  {
    name: 'Lucas Mueller',
    role: 'Startup Founder',
    country: 'Germany',
    image: '🇩🇪',
    score: 'Funded!',
    text: 'Had to pitch to US investors. Practiced my pitch dozens of times with instant feedback. Delivered it perfectly and secured our seed funding!',
  },
  {
    name: 'Yuki Yamamoto',
    role: 'Graduate Student',
    country: 'Japan',
    image: '🇯🇵',
    score: 'Thesis defended',
    text: 'Defended my thesis in English with confidence. The pronunciation practice made all the difference. My committee was impressed with my clarity.',
  },
];

const stats = [
  { value: '50,000+', label: 'Practice Sessions', description: 'completed by our users' },
  { value: '89%', label: 'Improvement Rate', description: 'see results in 30 days' },
  { value: '4.8/5', label: 'User Rating', description: 'from verified learners' },
];

const heroSlides = [
  {
    title: 'Ace Your IELTS',
    highlight: 'Speaking Test',
    description: 'Complete test simulations for Speaking, Writing, Reading & Listening. Get band score estimates and targeted improvement tips from our AI examiner.',
    gradient: 'from-emerald-400 to-teal-400',
    bgGradient: 'from-emerald-50 to-teal-50',
  },
  {
    title: 'Practice Your Speech',
    highlight: 'Before You Perform',
    description: 'Record your speech, get instant AI feedback on pronunciation and clarity, then perfect it before the big moment. Nail presentations, pitches, and public speaking.',
    gradient: 'from-blue-400 to-indigo-400',
    bgGradient: 'from-blue-50 to-indigo-50',
  },
  {
    title: 'Get Detailed Feedback',
    highlight: 'Fix Every Word',
    description: 'Our AI analyzes every sound you make. See exactly which words need work, how to position your mouth, and track your improvement over time.',
    gradient: 'from-purple-400 to-pink-400',
    bgGradient: 'from-purple-50 to-pink-50',
  },
];

const QUICK_TEST_STORAGE_KEY = 'litrlly_quick_test_shown';

const LandingPage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [statsIndex, setStatsIndex] = useState(0);
  const [showQuickTest, setShowQuickTest] = useState(false);

  // Auto-rotate hero slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // Auto-rotate stats on mobile
  useEffect(() => {
    const timer = setInterval(() => {
      setStatsIndex((prev) => (prev + 1) % stats.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  // Show quick test modal after 5 seconds (only once ever)
  useEffect(() => {
    const hasSeenQuickTest = localStorage.getItem(QUICK_TEST_STORAGE_KEY);
    if (hasSeenQuickTest) return;

    const timer = setTimeout(() => {
      setShowQuickTest(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleCloseQuickTest = () => {
    setShowQuickTest(false);
    localStorage.setItem(QUICK_TEST_STORAGE_KEY, 'true');
  };

  const openQuickTest = () => {
    setShowQuickTest(true);
  };

  const nextTestimonial = () => {
    setTestimonialIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setTestimonialIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Get visible testimonials (3 for desktop, 1 for mobile - handled by CSS)
  const getVisibleTestimonials = () => {
    const result = [];
    for (let i = 0; i < 3; i++) {
      result.push(testimonials[(testimonialIndex + i) % testimonials.length]);
    }
    return result;
  };

  return (
    <PublicLayout>
      {/* Hero Section with Slider */}
      <section className="relative overflow-hidden">
        {/* Watermark Logo - Top Right Corner */}
        <div className="absolute right-2 sm:right-4 lg:right-12 top-2 sm:top-4 lg:top-8 opacity-15 pointer-events-none">
          <WhaleLogo
            alt=""
            className="w-16 h-16 sm:w-24 sm:h-24 lg:w-36 lg:h-36 xl:w-48 xl:h-48"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-40">
          <div className="text-center relative" style={{ minHeight: '380px' }}>
            {heroSlides.map((slide, index) => (
              <div
                key={index}
                className={`transition-all duration-700 ${
                  currentSlide === index
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none'
                }`}
              >
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 tracking-tight">
                  {slide.title}
                  <span className={`block text-transparent bg-clip-text bg-gradient-to-r ${slide.gradient} mt-2`}>
                    {slide.highlight}
                  </span>
                </h1>
                <p className="mt-8 max-w-3xl mx-auto text-xl sm:text-2xl text-gray-500 leading-relaxed">
                  {slide.description}
                </p>
              </div>
            ))}

            {/* Slider Dots */}
            <div className="flex justify-center gap-3 mt-10">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    currentSlide === index
                      ? 'bg-blue-400 w-10'
                      : 'bg-gray-200 hover:bg-gray-300 w-3'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={openQuickTest}
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg shadow-blue-600/30"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Try It Now - Free
              </button>
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-8 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                Sign Up
              </Link>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              No sign up required. Try it instantly!
            </p>

            {/* Trust Badge */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-emerald-200 shadow-sm">
                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-700">Reviewed by Certified TESOL Teachers</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-blue-200 shadow-sm">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-700">Developed with Speech Pathologists</span>
              </div>
            </div>
          </div>
        </div>

        {/* Background decoration - changes with slide */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -z-10 w-full max-w-6xl">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 aspect-square bg-gradient-to-r ${slide.bgGradient} rounded-full blur-3xl transition-opacity duration-700 ${
                currentSlide === index ? 'opacity-70' : 'opacity-0'
              }`}
              style={{ transform: 'scale(1.2)' }}
            />
          ))}
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="py-16 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop: Grid layout */}
          <div className="hidden md:grid md:grid-cols-3 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="p-6">
                <div className="text-4xl sm:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  {stat.value}
                </div>
                <div className="mt-2 text-lg font-semibold text-gray-900">{stat.label}</div>
                <div className="text-sm text-gray-500">{stat.description}</div>
              </div>
            ))}
          </div>

          {/* Mobile: Carousel */}
          <div className="md:hidden">
            <div className="relative overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${statsIndex * 100}%)` }}
              >
                {stats.map((stat, index) => (
                  <div key={index} className="w-full flex-shrink-0 p-6 text-center">
                    <div className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                      {stat.value}
                    </div>
                    <div className="mt-2 text-lg font-semibold text-gray-900">{stat.label}</div>
                    <div className="text-sm text-gray-500">{stat.description}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-4">
              {stats.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setStatsIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === statsIndex
                      ? 'bg-blue-500 w-6'
                      : 'bg-gray-300 w-2'
                  }`}
                  aria-label={`Go to stat ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-4">
              Real Results
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Success Stories from Our Learners
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              See how Litrlly has helped thousands improve their English pronunciation and achieve their goals.
            </p>
          </div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Navigation Arrows */}
            <button
              onClick={prevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
              aria-label="Previous testimonial"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors"
              aria-label="Next testimonial"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Cards Container */}
            <div className="px-8 md:px-16">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {getVisibleTestimonials().map((testimonial, index) => (
                  <div
                    key={`${testimonialIndex}-${index}`}
                    className={`relative p-6 md:p-8 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 ${
                      index > 0 ? 'hidden md:block' : ''
                    }`}
                  >
                    {/* Quote icon */}
                    <div className="absolute top-4 right-4 md:top-6 md:right-6 text-4xl text-gray-100">"</div>

                    {/* 5 Stars */}
                    <div className="flex gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>

                    {/* Success badge */}
                    <div className="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full mb-3">
                      {testimonial.score}
                    </div>

                    <p className="text-gray-600 mb-6 relative z-10 text-sm md:text-base line-clamp-4">
                      "{testimonial.text}"
                    </p>

                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-gray-100 rounded-full flex items-center justify-center text-xl md:text-2xl">
                        {testimonial.image}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-sm md:text-base">{testimonial.name}</div>
                        <div className="text-xs md:text-sm text-gray-500">{testimonial.role} • {testimonial.country}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setTestimonialIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === testimonialIndex
                      ? 'bg-blue-500 w-6'
                      : 'bg-gray-300 hover:bg-gray-400 w-2'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              How It Works
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Three simple steps to perfect your speech
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Record Your Speech</h3>
              <p className="text-gray-600">
                Practice your presentation, speech, or any text. Our system captures every detail in high quality.
              </p>
            </div>

            {/* Step 2 */}
            <div className="relative p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Get Instant Feedback</h3>
              <p className="text-gray-600">
                AI analyzes every word, highlighting what to fix and showing exactly how to improve each sound.
              </p>
            </div>

            {/* Step 3 */}
            <div className="relative p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Perfect & Perform</h3>
              <p className="text-gray-600">
                Practice until you're confident, then deliver your speech with clarity and precision.
              </p>
            </div>
          </div>

          {/* Try It Button */}
          <div className="mt-12 text-center">
            <button
              onClick={openQuickTest}
              className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
            >
              Try It Now - No Sign Up
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Powerful Features
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to master English pronunciation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="p-6 bg-white border border-gray-100 rounded-xl hover:border-blue-200 transition-colors">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Phoneme Analysis</h3>
              <p className="text-gray-600 text-sm">
                Get detailed scores for each sound you pronounce, with visual feedback showing exactly what to improve.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="p-6 bg-white border border-gray-100 rounded-xl hover:border-blue-200 transition-colors">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Prosody & Intonation</h3>
              <p className="text-gray-600 text-sm">
                Learn proper rhythm, stress patterns, and intonation to sound more natural and fluent.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="p-6 bg-white border border-gray-100 rounded-xl hover:border-blue-200 transition-colors">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Coaching Tips</h3>
              <p className="text-gray-600 text-sm">
                Receive personalized suggestions on how to position your tongue, lips, and mouth for each sound.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="p-6 bg-white border border-gray-100 rounded-xl hover:border-blue-200 transition-colors">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Interview Practice</h3>
              <p className="text-gray-600 text-sm">
                Prepare for job interviews with AI-generated questions and professional feedback on your responses.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="p-6 bg-white border border-gray-100 rounded-xl hover:border-blue-200 transition-colors">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Progress Tracking</h3>
              <p className="text-gray-600 text-sm">
                Monitor your improvement over time with detailed statistics and pronunciation history.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="p-6 bg-white border border-gray-100 rounded-xl hover:border-blue-200 transition-colors">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Word Breakdown</h3>
              <p className="text-gray-600 text-sm">
                See how each word and syllable is pronounced with color-coded accuracy indicators.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Trust Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 text-sm font-medium rounded-full mb-4">
              Our Methodology
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Built by Experts, Powered by AI
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Our curriculum and feedback system are developed in collaboration with language education professionals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Certified Teachers */}
            <div className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Reviewed by Certified TESOL/TEFL Teachers
              </h3>
              <p className="text-gray-600 mb-4">
                Every aspect of our pronunciation curriculum has been reviewed and approved by certified English language teachers with 10+ years of experience.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Phoneme accuracy standards
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  IELTS scoring alignment
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Feedback language quality
                </li>
              </ul>
            </div>

            {/* Speech Science */}
            <div className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Developed with Speech Pathologists
              </h3>
              <p className="text-gray-600 mb-4">
                Our AI feedback system is built on speech pathology research, ensuring the tips you receive are scientifically accurate and actionable.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Mouth positioning guidance
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Tongue placement tips
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Breath control techniques
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-6">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-sm text-white/90">50,000+ practice sessions completed</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Your Journey to Clear English<br />
            <span className="text-blue-200">Starts Today</span>
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Join learners from 100+ countries who improved their pronunciation using our expert-reviewed, AI-powered platform.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={openQuickTest}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-lg font-medium rounded-xl text-blue-600 bg-white hover:bg-blue-50 transition-colors shadow-lg"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Try It Now - Free
            </button>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-lg font-medium rounded-xl text-white hover:bg-white/10 transition-colors"
            >
              Create Account
            </Link>
          </div>

          <p className="mt-6 text-sm text-blue-200">
            No sign up required • Instant feedback • Try all features
          </p>
        </div>
      </section>

      {/* Quick Test Modal */}
      <QuickTestModal isOpen={showQuickTest} onClose={handleCloseQuickTest} />
    </PublicLayout>
  );
};

export default LandingPage;
